/* eslint-disable lingui/no-unlocalized-strings --
 * Keyboard key labels ('Ctrl', 'Enter', 'Shift', 'a', '⌘', etc.) reflect the
 * physical key a user presses and are kept untranslated, matching peer apps
 * (GitHub, Linear, Gmail). Human-readable descriptions are wrapped with t``.
 */
import { t } from '@lingui/core/macro';
import { isMacPlatform } from '../../utils/platformUtils';

export type ShortcutSection = 'global' | 'list' | 'reader';

export interface ShortcutEntry {
  keys: string[];
  description: string;
}

export interface ShortcutGroup {
  section: ShortcutSection;
  title: string;
  entries: ShortcutEntry[];
}

/**
 * Literal key characters for the shortcut-bearing actions. Single source of
 * truth shared by the help modal entries below and the action-button tooltips
 * (see the ButtonIcon `shortcut` prop in ReaderHeader and HeaderActions), so a
 * key only has to change in one place.
 */
export const SHORTCUT_KEYS = {
  addLink: 'K',
  archive: 'a',
  like: 'l',
  move: 'm',
  tag: 't',
  edit: 'e',
  delete: 'd',
  copyLink: 'C',
} as const;

/** Modifier-key label for the current platform (⌘ on Mac, Ctrl elsewhere). */
export function modKey(): string {
  return isMacPlatform() ? '⌘' : 'Ctrl';
}

// Called at render time so t`` picks up the active locale.
// Keep this list in sync with:
//   - src/hooks/useReaderKeyboardShortcuts.ts (reader shortcuts)
//   - src/components/Reader/ReaderHeader.tsx (Esc to exit reader view)
//   - src/components/PageHeader.tsx (Cmd/Ctrl+K)
//   - src/hooks/useBookmarkListKeyboardNav.ts (list arrow navigation)
//   - src/components/Bookmark/Row/hooks/useBookmarkInteractions.ts (shift arrow selection)
//   - src/routes/_app.tsx (? to open this modal)
//   - src/components/CollapsibleSearchBar.tsx (/ to open search)
//   - src/components/Reader/ReaderHeader.tsx (tooltip key badges)
//   - src/components/HeaderActions.tsx (Add-link tooltip key badge)
export function getShortcutGroups(): ShortcutGroup[] {
  const mod = modKey();

  return [
    {
      section: 'global',
      title: t`Global`,
      entries: [
        { keys: [mod, SHORTCUT_KEYS.addLink], description: t`Add link` },
        { keys: ['/'], description: t`Search` },
        { keys: ['?'], description: t`Show keyboard shortcuts` },
      ],
    },
    {
      section: 'list',
      title: t`Article list`,
      entries: [
        { keys: ['↑'], description: t`Previous article` },
        { keys: ['↓'], description: t`Next article` },
        { keys: ['Enter'], description: t`Open article` },
        { keys: [mod, 'A'], description: t`Select all articles` },
        { keys: ['Shift', '↑'], description: t`Extend selection up` },
        { keys: ['Shift', '↓'], description: t`Extend selection down` },
      ],
    },
    {
      section: 'reader',
      title: t`Reader`,
      entries: [
        { keys: [SHORTCUT_KEYS.archive], description: t`Archive` },
        { keys: [SHORTCUT_KEYS.like], description: t`Toggle like` },
        { keys: [SHORTCUT_KEYS.move], description: t`Move to folder` },
        { keys: [SHORTCUT_KEYS.tag], description: t`Tags` },
        { keys: [SHORTCUT_KEYS.edit], description: t`Edit article` },
        { keys: [SHORTCUT_KEYS.delete], description: t`Delete` },
        { keys: [mod, SHORTCUT_KEYS.copyLink], description: t`Copy link` },
        { keys: ['Esc'], description: t`Exit reader view` },
      ],
    },
  ];
}
